import {
  Directive,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ContentChildren,
  QueryList,
  AfterContentInit,
  NgZone,
} from '@angular/core';
import { zip, Subscription } from 'rxjs';
import { AmapMarkerService } from './amap-marker.service';
import { LoggerService } from '../../shared/logger/logger.service';
import { AMapOverlay, OverlayOptions } from '../../base/amap-overlay';
import { EventBinderService } from '../../shared/event-binder.service';
import { getOptions, ChangeFilter } from '../../utils';
import { IPixel, IIcon, IMarkerLabel } from '../../interfaces';
import { PixelService } from '../../shared/pixel.service';
import { IconService } from '../../shared/icon.service';
import { MarkerLabelService } from '../../shared/marker-label.service';
import { AmapInfoWindowComponent } from '../../components/amap-info-window/amap-info-window.component';
import { AMapService } from '../../shared/amap.service';

const TAG = 'amap-marker';
const ALL_OPTIONS = [
  ...OverlayOptions,
  'position',
  'anchor',
  'offset',
  'icon',
  'content',
  'topWhenClick',
  'raiseOnDrag',
  'visible',
  'zIndex',
  'angle',
  'autoRotation',
  'animation',
  'shadow',
  'title',
  'shape',
  'label',
];

@Directive({
  selector: 'amap-marker',
  exportAs: 'marker',
  providers: [AmapMarkerService],
})
export class AmapMarkerDirective extends AMapOverlay<AMap.Marker>
  implements OnChanges, OnDestroy, AfterContentInit {
  // ---- Marker Options ----
  /**
   * ç¹æ è®°å¨å°å¾ä¸æ¾ç¤ºçä½ç½®
   */
  @Input() position: AMap.LocationValue;
  /**
   * æ è®°éç¹
   */
  @Input() anchor: AMap.Marker.Anchor;
  /**
   * ç¹æ è®°æ¾ç¤ºä½ç½®åç§»é
   */
  @Input() offset: AMap.Pixel | IPixel;
  /**
   * éå¨ç¹æ è®°ä¸­æ¾ç¤ºçå¾æ 
   */
  @Input() icon: string | AMap.Icon | IIcon;
  /**
   * ç¹æ è®°æ¾ç¤ºåå®¹
   */
  @Input() content: string | HTMLElement;
  /**
   * é¼ æ ç¹å»æ¶markeræ¯å¦ç½®é¡¶
   */
  @Input() topWhenClick: boolean;
  /**
   * ææ½ç¹æ è®°æ¶æ¯å¦å¼å¯ç¹æ è®°ç¦»å¼å°å¾çææ
   */
  @Input() raiseOnDrag: boolean;
  /**
   * ç¹æ è®°æ¯å¦å¯è§
   */
  @Input() visible: boolean;
  /**
   * ç¹æ è®°çå å é¡ºåº
   */
  @Input() zIndex: number;
  /**
   * ç¹æ è®°çæè½¬è§åº¦
   */
  @Input() angle: number;
  /**
   * æ¯å¦èªå¨æè½¬
   */
  @Input() autoRotation: boolean;
  /**
   * ç¹æ è®°çå¨ç»ææ
   */
  @Input() animation: AMap.AnimationName;
  /**
   * ç¹æ è®°é´å½±
   */
  @Input() shadow: AMap.Icon | string | IIcon;
  /**
   * é¼ æ æ»è¿ç¹æ è®°æ¶çæå­æç¤º
   */
  @Input() title: string;
  /**
   * å¯ç¹å»åºå
   */
  @Input() shape: AMap.MarkerShape;
  /**
   * ææ¬æ æ³¨
   */
  @Input() label: AMap.Marker.Label | IMarkerLabel;
  /**
   * é¢å¤: æ¯å¦ç½®é¡¶
   */
  @Input() isTop: boolean;
  /**
   * é¢å¤: æ¯å¦éè
   */
  @Input() hidden = false;
  /**
   * é¢å¤: æ¯å¦åå«å¨ç¹èåä¸­
   */
  @Input() inCluster = false;
  /**
   * é¢å¤: ç¹å»æ¶æ¯å¦æ¾ç¤ºä¿¡æ¯çªä½
   */
  @Input() openInfoWindow = true;

  // amap-marker events:
  @Output() naReady = new EventEmitter();
  @Output() naMouseOut: EventEmitter<any>;
  @Output() naDragStart: EventEmitter<any>;
  @Output() naDragging: EventEmitter<any>;
  @Output() naDragEnd: EventEmitter<any>;
  @Output() naMoving: EventEmitter<any>;
  @Output() naMoveEnd: EventEmitter<any>;
  @Output() naMoveAlong: EventEmitter<any>;

  // amap info window:
  @ContentChildren(AmapInfoWindowComponent)
  infoWindowComponent = new QueryList<AmapInfoWindowComponent>();

  private inited = false;
  private subscription: Subscription;

  constructor(
    protected os: AmapMarkerService,
    protected binder: EventBinderService,
    private amaps: AMapService,
    private pixels: PixelService,
    private icons: IconService,
    private mlabels: MarkerLabelService,
    private logger: LoggerService,
    private ngZone: NgZone,
  ) {
    super(os, binder);
    const target = this.os.get();
    this.naMouseOut = this.binder.bindEvent(target, 'mouseout');
    this.naDragStart = this.binder.bindEvent(target, 'dragstart');
    this.naDragging = this.binder.bindEvent(target, 'dragging');
    this.naDragEnd = this.binder.bindEvent(target, 'dragend');
    this.naMoving = this.binder.bindEvent(target, 'moving');
    this.naMoveEnd = this.binder.bindEvent(target, 'moveend');
    this.naMoveAlong = this.binder.bindEvent(target, 'movealong');
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.os.destroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    const filter = ChangeFilter.of(changes);
    const marker = this.get();
    if (!this.inited) {
      // do not draw marker when no poistion defined.
      if (!this.position) {
        return;
      }
      this.amaps.get().subscribe(() => {
        this.logger.d(TAG, 'initializing ...');
        // bind info window events:
        this.subscription = this.binder.bindEvent(marker, 'click').subscribe(() => {
          if (this.openInfoWindow) {
            this.infoWindowComponent.forEach(w => w.open());
          }
        });
        const options = getOptions<AMap.Marker.Options>(this, ALL_OPTIONS);
        if (this.icon) {
          options.icon = this.icons.create(this.icon);
        }
        if (this.shadow) {
          options.shadow = this.icons.create(this.shadow);
        }
        if (this.label) {
          options.label = this.mlabels.create(this.label);
        }
        if (this.offset) {
          options.offset = this.pixels.create(this.offset);
        }
        this.logger.d(TAG, 'options:', options);
        this.os.create(options, !this.inCluster).subscribe(m => {
          this.ngZone.run(() => this.naReady.emit(m));
          this.logger.d(TAG, 'marker is ready.');
        });
        this.inited = true;
        this.updateInfoWindow();
        this.updateInfoWindowPosition();
      });
    } else {
      zip(filter.has<string | AMap.Icon | IIcon>('icon'), marker).subscribe(([v, m]) =>
        m.setIcon(this.icons.create(v)),
      );
      zip(filter.has<string | AMap.Icon>('shadow'), marker).subscribe(([v, m]) =>
        m.setShadow(this.icons.create(v)),
      );
      zip(filter.has<AMap.Marker.Label | IMarkerLabel>('label'), marker).subscribe(([v, m]) =>
        m.setLabel(this.mlabels.create(v)),
      );
      zip(filter.has<string>('title'), marker).subscribe(([v, m]) => m.setTitle(v));
      zip(filter.has<any>('content'), marker).subscribe(([v, m]) => m.setContent(v));
      zip(filter.has<any>('extData'), marker).subscribe(([v, m]) => m.setExtData(v));
      zip(filter.has<boolean>('clickable'), marker).subscribe(([v, m]) => m.setClickable(!!v));
      zip(filter.has<boolean>('draggable'), marker).subscribe(([v, m]) => m.setDraggable(!!v));
      zip(filter.has<boolean>('visible'), marker).subscribe(([v, m]) => (v ? m.show() : m.hide()));
      zip(filter.has<string>('cursor'), marker).subscribe(([v, m]) => m.setCursor(v));
      zip(filter.has<AMap.AnimationName>('animation'), marker).subscribe(([v, m]) =>
        m.setAnimation(v),
      );
      zip(filter.has<number>('angle'), marker).subscribe(([v, m]) => m.setAngle(v));
      zip(filter.has<number>('zIndex'), marker).subscribe(([v, m]) => m.setzIndex(v));
      zip(filter.has<AMap.MarkerShape>('shape'), marker).subscribe(([v, m]) => m.setShape(v));
      zip(filter.notEmpty<AMap.Pixel | IPixel>('offset'), marker).subscribe(([v, m]) =>
        m.setOffset(this.pixels.create(v)),
      );
      zip(filter.notEmpty<AMap.LocationValue>('position'), marker).subscribe(([v, m]) =>
        m.setPosition(v),
      );
    }
    zip(filter.has<boolean>('isTop'), marker).subscribe(([v, m]) => m.setTop(!!v));
    zip(filter.has<boolean>('hidden'), marker).subscribe(([v, m]) => (v ? m.hide() : m.show()));
  }

  ngAfterContentInit() {
    this.updateInfoWindow();
    this.infoWindowComponent.changes.subscribe(() => this.updateInfoWindow());
  }

  private updateInfoWindow() {
    if (this.infoWindowComponent && this.inited) {
      if (this.infoWindowComponent.length > 1) {
        this.logger.e(TAG, 'Expected no more than 1 info window.');
        return;
      }

      const marker = this.os.get();
      this.infoWindowComponent.forEach(component => {
        component.hostMarker = marker;
      });
    }
  }

  private updateInfoWindowPosition() {
    if (this.infoWindowComponent && this.inited) {
      this.infoWindowComponent.forEach(component => {
        component.toggleOpen();
      });
    }
  }

  /**
   * è·åå·²åå»ºç AMap.Marker å¯¹è±¡
   */
  get() {
    return this.os.get();
  }
}
